import type { Customer, Invoice, Pricing, StripeHandler } from '@alliance-disposal/transport-types';
import * as Sentry from '@sentry/astro';
import axios from 'axios';

export const endPoints = {
    SEND_EMAIL: 'send-email',
};

export class APIService {
    private static BASE_REST_URL = import.meta.env.PUBLIC_BASE_URL;

    static Email = {
        sendEmail: (opts: {
            endPoint: 'send-email';
            emailData: {
                toAddress: string;
                sender: string;
                subject: string;
                replyToAddress: string;
                htmlMessage: string;
                textMessage: string;
                bcc?: string;
            };
        }): Promise<boolean> => {
            //   if (
            //     data.replyToAddress?.includes("hpeter555555") ||
            //     data.replyToAddress === "hpeter555555@yahoo.com" ||
            //     data.replyToAddress === "hpeter555555@yahoo.co"
            //   ) {
            //     return true;
            //   }

            const url = 'https://xb0o6vukp2.execute-api.us-east-1.amazonaws.com/production/' + opts.endPoint;
            return axios({
                method: 'POST',
                url: url,
                data: JSON.stringify(opts.emailData),
            });
        },
    };
    static Lead = {
        createLead: async (data: Customer.AllianceLeadPublicCreateTransport): Promise<string> => {
            const leadRes = await axios({
                method: 'POST',
                url: APIService.BASE_REST_URL + '/customer/alliance-leads/public',
                data: data,
            });

            return leadRes.data;
        },
        fetchLead: async (id: string): Promise<Customer.LeadCheckoutTransport> => {
            return (
                await axios<Customer.LeadCheckoutTransport>({
                    method: 'GET',
                    url: APIService.BASE_REST_URL + `/customer/checkout/${id}`,
                })
            ).data;
        },
    };
    static Order = {
        checkout: async (data: StripeHandler.CustomerCreateMultipleOrdersInput) => {
            const checkout = await axios({
                method: 'POST',
                url: APIService.BASE_REST_URL + '/stripe/order/public',
                data: data,
            });

            return checkout.data;
        },
        getCoupons: async (opts: { code: string; email?: string }) => {
            const url = APIService.BASE_REST_URL + '/invoice/sourgum-public/coupon/' + opts.code;

            const { data } = await axios.get<Invoice.CouponTransport>(url);

            return data;
        },
    };
    static Pricing = {
        fetch: async (
            coordinates: { lat: number; lng: number },
            taxQuery?: {
                zip?: string;
                state: string;
            },
        ): Promise<Pricing.AnonymousPricingTransport> => {
            try {
                const { data } = await axios.get<Pricing.AnonymousPricingTransport[]>(
                    APIService.BASE_REST_URL + `/pricing/by-service-location`,
                    {
                        params: {
                            ...coordinates,
                            ...taxQuery,
                        },
                    },
                );

                if (data.length > 1) {
                    throw new Error('Error fetching pricing zone. Too many zones');
                }

                const returnData = data[0];

                if (!returnData) {
                    throw new Error('Error fetching pricing zone. No zones');
                }

                returnData.pricingData = returnData.pricingData.filter(
                    (material) => material.allowOnlineCheckout && !material.doesNotService,
                );
                return returnData;
            } catch (e) {
                Sentry.captureException(e);
                throw e;
            }
        },
    };
}

export interface StripeCoupon {
    object: string;
    data: Data[];
    has_more: boolean;
    url: string;
}

export interface Data {
    id: string;
    object: string;
    active: boolean;
    code: string;
    coupon: Coupon;
    created: number;
    customer: Record<string, unknown>;
    expires_at: Record<string, unknown>;
    livemode: boolean;
    max_redemptions: Record<string, unknown>;
    metadata: Record<string, unknown>;
    restrictions: Restrictions;
    times_redeemed: number;
}

export interface Coupon {
    id: string;
    object: string;
    amount_off: number;
    created: number;
    currency: string;
    duration: string;
    duration_in_months: Record<string, unknown>;
    livemode: boolean;
    max_redemptions: Record<string, unknown>;
    metadata: Record<string, unknown>;
    name: string;
    percent_off: number;
    redeem_by: Record<string, unknown>;
    times_redeemed: number;
    valid: boolean;
}

export interface Restrictions {
    first_time_transaction: boolean;
    minimum_amount: Record<string, unknown>;
    minimum_amount_currency: Record<string, unknown>;
}
